import React, { useEffect, useState } from 'react';
import { Button } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Plot from 'react-plotly.js';
import moment from 'moment';
import 'moment/locale/pt-br';
import { extractTransectDataFromMap } from '../../../common/map';
import HovmollerChartDialog from '../../components/dialog/HovmollerChartDialog';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import ViewComfyOutlinedIcon from '@material-ui/icons/ViewComfyOutlined';

/**
 * @function HovmollerPlot
 * @description React component that generates and displays a Hovmöller plot based on shoreline transect data.
 * The plot shows the temporal evolution of average transect positions relative to their historical mean.
 * @see {@link handleGenerateHovmoller} - builds the plot data from the transect matrix.
 * **Called by:
 * @see {ProcessingPage} - Shoreline module's processing page.
 * @see {ProcessingShoreRPage} - ShoreR module's processing page.
 * @memberof module:SHARED
 * @returns {JSX.Element} The Hovmöller plot component.
 */
const HovmollerPlot = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const chartLang = t('forms.transectEvolution.chartLanguage');
  const isPortuguese = chartLang.toLowerCase().includes('portugu');
  moment.locale(isPortuguese ? 'pt-BR' : 'en');

  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (!showPopup) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowPopup(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPopup]);
  const [plotData, setPlotData] = useState(null);
  const [plotHeight, setPlotHeight] = useState(500);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  /**
   * @function handleGenerateHovmoller
   * @description Processes transect data and generates the Hovmöller plot:
   * 1. Extracts transect data
   * 2. Groups distances by date and transect
   * 3. Calculates historical mean per transect
   * 4. Computes deviation from the mean
   * 5. Builds z matrix for heatmap
   * 6. Configures color scale and layout
   * 7. Updates state to render the plot
   * @see {@link extractTransectDataFromMap}
   * **Called by:
   * @see {HovmollerPlot} - via the "Hovmöller plot" button's onClick.
   * @memberof module:SHARED
   * @returns {void}
   */
  const handleGenerateHovmoller = () => {
    const { transectMatrix } = extractTransectDataFromMap();
    if (!transectMatrix || transectMatrix.length === 0) return;

    const hovData = {};

    transectMatrix.forEach((transect) => {
      transect.distances.forEach(({ date, distance }) => {
        const label = moment(date).format('YYYY-MM-DD');
        if (!hovData[label]) hovData[label] = {};
        hovData[label][transect.id] = hovData[label][transect.id]
          ? [...hovData[label][transect.id], distance]
          : [distance];
      });
    });

    const x = Object.keys(hovData).sort();

    const y = Array.from(new Set(transectMatrix.map((t) => t.id))).sort((a, b) => {
      const numA = parseInt(a.match(/\d+/)?.[0] || '0', 10);
      const numB = parseInt(b.match(/\d+/)?.[0] || '0', 10);
      return numA - numB;
    });

    const calculatedHeight = Math.min(Math.max(500, y.length * 20), 1200);
    setPlotHeight(calculatedHeight);

    const transectMeans = {};
    y.forEach((transectId) => {
      const allValues = x.flatMap((label) => hovData[label]?.[transectId] || []);
      transectMeans[transectId] = allValues.length ? allValues.reduce((a, b) => a + b, 0) / allValues.length : 0;
    });

    const z = y.map((transectId) =>
      x.map((label) => {
        const values = hovData[label]?.[transectId];
        if (!values || values.length === 0) return null;
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        return avg - transectMeans[transectId];
      }),
    );

    const flatZ = z.flat().filter((v) => v !== null);
    const zmin = Math.floor(Math.min(...flatZ));
    const zmax = Math.ceil(Math.max(...flatZ));
    const zeroPosition = Math.abs(zmin) / (Math.abs(zmin) + Math.abs(zmax));
    setPlotData({
      data: [
        {
          z,
          x,
          y,
          type: 'contour',
          colorscale: [
            [0.0, 'rgb(109,1,33)'],
            [zeroPosition * 0.5, 'rgb(226,123,97)'],
            [zeroPosition, 'rgb(255,255,255)'],
            [zeroPosition + (1 - zeroPosition) * 0.5, 'rgb(145,195,222)'],
            [1.0, 'rgb(34,105,174)'],
          ],
          contours: {
            coloring: 'heatmap',
            showlabels: true,
            line: {
              color: 'rgba(0, 0, 0, 0.06)',
            },
          },
          zmin,
          zmax,
          colorbar: {
            title: {
              text: t('forms.hovmoller.colorbarTitle'),
              side: 'right',
            },
            tickvals: [zmin, 0, zmax],
            ticktext: [zmin.toString(), '0', zmax.toString()],
          },
        },
      ],
      layout: {
        title: t('forms.hovmoller.hovmollerPlot'),
        xaxis: {
          title: t('forms.hovmoller.xAxisLabel'),
          tickangle: -45,
          tickfont: { size: 10 },
        },
        yaxis: {
          title: t('forms.hovmoller.yAxisLabel'),
          tickfont: { size: 10 },
        },
        autosize: true,
        margin: { b: 100 },
      },
    });

    setShowPopup(true);
  };

  return (
    <>
      <Button
        fullWidth
        color="primary"
        variant="contained"
        startIcon={<ViewComfyOutlinedIcon />}
        onClick={handleGenerateHovmoller}>
        {t('forms.hovmoller.hovmollerPlot')}
      </Button>

      {showPopup && plotData && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}>
          <HovmollerChartDialog open={showHelpDialog} close={() => setShowHelpDialog(false)} />
          <div
            style={{
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
              padding: 24,
              borderRadius: 8,
              width: '90%',
              maxWidth: 800,
              boxShadow: '0 0 20px rgba(0,0,0,0.3)',
              textAlign: 'center',
            }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}>
              <h3 style={{ margin: 0 }}>{t('forms.hovmoller.popupTitle')}</h3>
              <Button onClick={() => setShowHelpDialog(true)} style={{ minWidth: 32, padding: 6 }}>
                <HelpOutlineIcon />
              </Button>
            </div>

            <Plot
              data={plotData.data}
              layout={{
                ...plotData.layout,
                xaxis: {
                  ...plotData.layout.xaxis,
                  tickformat: isPortuguese ? '%d/%m/%Y' : '%m/%d/%Y',
                  hoverformat: isPortuguese ? '%d/%m/%Y' : '%m/%d/%Y',
                  type: 'date',
                },
              }}
              style={{ width: '100%', height: `${Math.round(plotHeight)}px` }}
              config={{ responsive: true }}
            />

            <Button variant="outlined" color="secondary" onClick={() => setShowPopup(false)} style={{ marginTop: 20 }}>
              {t('forms.hovmoller.close')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default HovmollerPlot;
